import React, { useMemo } from 'react';

import { Skeleton, useMediaQuery } from '@mui/material';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { DATE_FORMATS } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery } from 'utils/react-query';

import globalQueries from 'containers/Globals/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, FILTER_TYPES, TP } from 'operabase-router/constants';

import { COMPONENTS, SECTIONS } from '../Analytics/constants';
import NoResult from '../NoResult';

import classes from './ContextualFootLinks.module.scss';

const getDateRange = value => {
  const date = createDate(value);

  if (/^\d{4}$/.test(value)) {
    return {
      [FILTER_TYPES.DATE_FROM]: date.startOf('year').format(DATE_FORMATS.FULL_DATE),
      [FILTER_TYPES.DATE_TO]: date.endOf('year').format(DATE_FORMATS.FULL_DATE),
    };
  }

  if (/^\d{4}-\d{2}$/.test(value)) {
    return {
      [FILTER_TYPES.DATE_FROM]: date.startOf('month').format(DATE_FORMATS.FULL_DATE),
      [FILTER_TYPES.DATE_TO]: date.endOf('month').format(DATE_FORMATS.FULL_DATE),
    };
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return {
      [FILTER_TYPES.DATE_FROM]: date.format(DATE_FORMATS.FULL_DATE),
      [FILTER_TYPES.DATE_TO]: date.endOf('month').format(DATE_FORMATS.FULL_DATE),
    };
  }

  return {};
};

const getFilters = ({ searchEntity, entity = {} }) => {
  let filters = {};
  const { entityType: searchEntityType } = searchEntity || {};
  const { id: entityId, entityType: entityEntityType, slug: entitySlug } = entity || {};

  const addWhereFilter = ({ entity: option, entityType, country = null }) => {
    filters[FILTER_TYPES.WHERE] = [
      ...(filters[FILTER_TYPES.WHERE] || []),
      {
        ...(country && { country }),
        entity: option,
        entityType,
      },
    ];
  };

  filters =
    searchEntityType === ENTITY_TYPES.PROFILE
      ? {
          [FILTER_TYPES.WHO]: [
            {
              entity: searchEntity,
              entityType: ENTITY_TYPES.PROFILE,
            },
          ],
        }
      : {
          [FILTER_TYPES.WHERE]: [
            {
              entity: searchEntity,
              entityType: ENTITY_TYPES.ORGANIZATION,
            },
          ],
        };

  if (entityId && entityEntityType === ENTITY_TYPES.COUNTRY) {
    addWhereFilter({ entity, entityType: ENTITY_TYPES.COUNTRY });
  }

  if (entityId && entityEntityType === ENTITY_TYPES.CITY) {
    addWhereFilter({ entity, entityType: ENTITY_TYPES.CITY, country: entity?.country });
  }

  if (entityId && entityEntityType === ENTITY_TYPES.WORK_TYPE) {
    filters[FILTER_TYPES.WHAT] = [
      {
        entity,
        entityType: ENTITY_TYPES.WORK,
      },
    ];
  }

  if (entitySlug && entityEntityType === ENTITY_TYPES.DATE) {
    filters[FILTER_TYPES.DATE] = getDateRange(entitySlug);
  }

  return filters;
};

const ContextualFootLinks = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext, location } = useAppContext();
  const navigate = useNavigate();
  const { permissions } = usePageContext();
  const isMediumScreen = useMediaQuery('(max-width: 1000px)');
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const { isMobile } = useDeviceTypeLayouts();
  const { basePath, page, filterParams } = obRouteContext || {};
  const { castingToolPermissions, isAdmin } = permissions;
  const hasCastingToolAccess = isAdmin || castingToolPermissions?.hasAccess;
  const { entity, entityType } = page || {};
  const { country } = location;
  const limit = useMemo(() => {
    if (isMobile) {
      return 14;
    }

    if (isMediumScreen || isSmallScreen) {
      return 28;
    }

    return 42;
  }, [isMediumScreen, isMobile, isSmallScreen]);

  const { data: recommendations = [], isLoading } = useQuery(
    globalQueries.getRecommendationsInformation({
      filters: {
        ...(country && { user_country_id: country?.id }),
        ...(!basePath && Object.keys(filterParams || {}).length === 0 && { is_homepage: true }),
        ...(filterParams?.[FILTER_TYPES.CTX_ENTITY] && {
          [FILTER_TYPES.SEARCH_EXPRESSION]: filterParams?.[FILTER_TYPES.CTX_ENTITY],
        }),
      },
      limit,
      queryConfig: {
        enabled: location?.isReady && obRouteContext?.isReady,
      },
    }),
  );

  const recs = recommendations?.data || [];

  if (isLoading && !recs.length) {
    return (
      <div className={classes.container}>
        <Skeleton variant="rectangular" width="100%" height={100} />
      </div>
    );
  }

  if (!isLoading && !recs.length) {
    return (
      <div className={classes.container}>
        <NoResult title={t(`${TP}.NO_RESULTS`)} />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <ul className={classes.gridWrapper}>
        {recs.map(({ searchEntity, entities, id = '' }, idx) => (
          <li key={`${entities?.[0]?.id}-${idx}`}>
            <LinkButton
              variant="text"
              styles={{
                root: classes.gridItem,
              }}
              rightIcon={<SpriteIcon icon="chevron_right" size={12} />}
              {...navigate.getLinkProps({
                filters: getFilters({
                  searchEntity: searchEntity || { ...entity, entityType },
                  entity: entities?.[0],
                }),
                pro: hasCastingToolAccess,
              })}
              isLink
              scroll
              trackingData={{
                section: SECTIONS.CONTEXTUAL_FOOT_LINKS,
                component: COMPONENTS.NAVIGATE_CTA,
              }}
            >
              <Typography size="12" color="secondary">
                {`${searchEntity?.name || entity?.name} ${t(`${TP}.FN_IN`)} ${
                  id.includes('datemonth')
                    ? createDate(entities?.[0]?.name).format(DATE_FORMATS.MONTH_YEAR)
                    : entities?.[0]?.name
                }`}
              </Typography>
            </LinkButton>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ContextualFootLinks;
